<script setup lang="ts">
import { useSessionsStore } from "~/store/sessions/sessions-store";
import { useSessions } from "~/composables/sessions/useSessions";
import StatusTag from "~/components/Common/StatusTag.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import Tooltip from "~/components/Common/Tooltip.vue";
import type { IStatusModal } from "~/utils/interfaces/sessions-interfaces";
import { useSessionIndividual } from "~/composables/sessions/useSessionIndividual";
import { useSessionLmsRules } from "~/composables/sessions/useSessionLmsRules";
import { SessionLmsAction } from "@simbelapp/order-sdk";

const props = defineProps({
  status: {
    type: String,
    value: undefined,
  },
});

const showPopupState = ref(false);
const sessions = useSessions();
const sessionsStore = useSessionsStore();
const sessionIndividual = useSessionIndividual();
const { isLmsActionDisabled } = useSessionLmsRules();
const data = computed(() => sessionIndividual.getSessionHeading());

const allowedActionsFiltered = computed(() => {
  return sessionsStore.trainingPlanSessionIndividual?.allowedActions.filter(
    (x) => x.name !== data.value.status && x.name !== "no_show",
  );
});

const handleAllowedAction = (name: keyof IStatusModal) => {
  sessionsStore.showSessionStatusActionModal[name] = true;
  showPopup();
};

const showPopup = () => {
  if (showPopupState.value) {
    showPopupState.value = false;
    document.removeEventListener("click", hidePopup);
  } else {
    showPopupState.value = true;
    document.addEventListener("click", hidePopup);
  }
};

const hidePopup = (event: MouseEvent) => {
  if (
    event.target instanceof Element &&
    !event.target.closest(".allowed-container") &&
    !event.target.closest(".edit-svg")
  ) {
    showPopupState.value = false;
    document.removeEventListener("click", hidePopup);
  }
};
</script>

<template>
  <SvgIcon
    v-if="!isLmsActionDisabled(SessionLmsAction.EditAttendeeStatus)"
    class="edit-svg"
    name="edit"
    @click="showPopup"
  />
  <div
    v-show="showPopupState"
    v-if="!isLmsActionDisabled(SessionLmsAction.EditAttendeeStatus)"
    class="allowed-container"
  >
    <div
      v-for="allowedAction in sessionsStore.trainingPlanSessionIndividual?.allowedActions"
      :key="`key-${allowedAction.name}`"
    >
      <div
        v-if="allowedAction.authorized"
        class="allowed-cell"
        @click="handleAllowedAction(allowedAction.name as keyof IStatusModal)"
      >
        <StatusTag :tag="sessions.statusMapping(allowedAction.name)" />
      </div>
      <Tooltip v-else append-to-body :position="'top-left'">
        <template #img>
          <div class="allowed-cell disabled" :data-allowed="allowedAction.authorized">
            <StatusTag :tag="sessions.statusMapping(allowedAction.name)" />
          </div>
        </template>
        <template #content>
          <div class="flex w-[358px] gap-[8px]">
            <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
            <span v-if="allowedAction.name === 'completed'"
              >Pour passer le participant au statut Terminé, rendez-vous sur la session et cliquez sur "Déclarer
              terminée".
            </span>
            <span v-else-if="allowedAction.name === 'canceled' && props.status === 'pending_provider'"
              >L’inscription a déjà été facturée. Contactez le support pour régulariser la situation.
            </span>
            <span v-else
              >Impossible de changer le statut de cette inscription. Contactez votre équipe RH pour régulariser la
              situation.
            </span>
          </div>
        </template>
      </Tooltip>
    </div>
  </div>
  <Tooltip v-if="isLmsActionDisabled(SessionLmsAction.EditAttendeeStatus)" :position="'top-mid'" minified>
    <template #img>
      <SvgIcon
        class="w-[16px] h-[16px] ml-2"
        name="edit"
        :disabled="isLmsActionDisabled(SessionLmsAction.EditAttendeeStatus)"
        @click="showPopup"
      />
    </template>
    <template #content>
      <div class="flex gap-[8px] items-center body_regular w-[338px]">
        <img src="/icons/emojis/eyes.svg" alt="" :style="{ height: '16px', width: '16px' }" />
        <span>Cette action n'est pas disponible pour une session importée d'un LMS</span>
      </div>
    </template>
  </Tooltip>
</template>

<style lang="scss" scoped>
.edit-svg {
  @apply w-[16px] h-[16px];
  color: var(--text-secondary-color);
  position: absolute;
  right: -25px;
  cursor: pointer;
}

.allowed-container {
  @apply flex flex-col items-start absolute;
  padding: 8px 0px;
  background: var(--white-main);
  box-shadow: var(--shadow-medium);
  border-radius: 8px;
  top: 39px;
  z-index: 1;
}

.allowed-cell {
  padding: 4px 8px;
  width: 150px;
  background: var(--white-main);

  &:hover {
    background: var(--grey-lightest);
    cursor: pointer;
  }

  animation: rotateX 300ms ease-in-out forwards;
  transform-origin: top center;
}

.disabled {
  cursor: default;

  &:hover {
    cursor: default;
  }
}
</style>
